import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { ReactNode } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { stopRoleMarker } from "@/components/Dispatch/context/routeFormat";
import type { RouteStopSummary } from "@/types/driverRoute";
import { DISPATCH_COLOR_VALUES } from "./dispatchColors";
import { TextCell } from "./TextCell";

/**
 * The Next Stop cell: the stop's name behind a colored letter for what the driver is heading to (shipper, consignee,
 * fuel stop, or T-call), the full word on hover; nothing at all without a name.
 *
 * The marker sets its color on its own element rather than the cell's root, so a column paint recolors the name and
 * never the marker.
 */
export function NextStopCell({
  value,
  role,
  field,
}: {
  value: string;
  role: RouteStopSummary["role"];
  /** The column's field, so a search scanning it marks what it matched in the stop's name. */
  field?: string;
}): ReactNode {
  if (!value) return null;
  const marker = stopRoleMarker(role);
  const color = marker ? DISPATCH_COLOR_VALUES[marker.color] : undefined;
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {marker && (
        <Tooltip title={marker.label}>
          <Box
            component="span"
            data-sb={TestIds.dispatch.nextStopMarker}
            data-role={role}
            aria-label={marker.label}
            sx={{
              flex: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 16,
              height: 16,
              borderRadius: "50%",
              bgcolor: `color-mix(in oklch, ${color} 20%, transparent)`,
              color,
              fontSize: "0.6875rem",
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {marker.letter}
          </Box>
        </Tooltip>
      )}
      <TextCell value={value} field={field} />
    </Box>
  );
}
